import PocketBase from 'pocketbase';

const pb = new PocketBase('https://api.simpleclair.fr');

// Force reload authStore from localStorage on module init
// This ensures the token is available even after page refresh
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('pocketbase_auth');
  if (stored) {
    try {
      const data = JSON.parse(stored);
      if (data.token && data.record) {
        pb.authStore.save(data.token, data.record);
      }
    } catch (e) {
      console.warn('Failed to restore auth from localStorage:', e);
    }
  }
}

export default pb;

// Types for our collections
export interface Lead {
  id: string;
  created: string;
  updated: string;
  
  // Contact info
  name: string;
  email: string;
  phone: string;
  company?: string;
  
  // Project details
  activity?: string;
  location?: string;
  description?: string;
  services?: string[];
  budget?: string;
  timeline?: string;
  
  // Pipeline status
  status: PipelineStage;
  
  // Tracking
  source?: string;
  notes?: string;
  last_contact?: string;
  next_followup?: string;
  
  // Files
  photos?: string[];
  logo?: string;
  documents?: string[];
}

export interface Communication {
  id: string;
  created: string;
  lead: string;
  type: 'email' | 'call' | 'sms' | 'meeting' | 'note';
  direction: 'inbound' | 'outbound';
  subject?: string;
  content: string;
  attachments?: string[];
}

export interface Reminder {
  id: string;
  created: string;
  lead: string;
  due_date: string;
  message: string;
  completed: boolean;
}

// Pipeline stages configuration - nouveaux statuts demandés
export const PIPELINE_STAGES = [
  { 
    id: 'nouveau', 
    label: 'Nouveau', 
    emoji: '🆕',
    color: 'bg-blue-500', 
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Nouveau lead entrant' 
  },
  { 
    id: 'contacte', 
    label: 'Contacté', 
    emoji: '📞',
    color: 'bg-amber-500', 
    textColor: 'text-amber-700',
    bgLight: 'bg-amber-50',
    borderColor: 'border-amber-200',
    description: 'Premier contact établi' 
  },
  { 
    id: 'qualifie', 
    label: 'Qualifié', 
    emoji: '✨',
    color: 'bg-indigo-500', 
    textColor: 'text-indigo-700',
    bgLight: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    description: 'Lead qualifié' 
  },
  { 
    id: 'converti', 
    label: 'Converti', 
    emoji: '✅',
    color: 'bg-green-500', 
    textColor: 'text-green-700',
    bgLight: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Client signé' 
  },
  { 
    id: 'perdu', 
    label: 'Perdu', 
    emoji: '❌',
    color: 'bg-red-500', 
    textColor: 'text-red-700',
    bgLight: 'bg-red-50',
    borderColor: 'border-red-200',
    description: 'Lead perdu' 
  },
] as const;

// Legacy mapping - pour compatibilité avec les anciens status
export const LEGACY_STATUS_MAP: Record<string, PipelineStage> = {
  'lead': 'nouveau',
  'contact': 'contacte',
  'devis': 'qualifie',
  'signe': 'converti',
  'livre': 'converti',
};

export type PipelineStage = typeof PIPELINE_STAGES[number]['id'];

// Helper pour normaliser un status (legacy ou nouveau)
export function normalizeStatus(status: string | undefined | null): PipelineStage {
  if (!status) return 'nouveau';
  
  // Si c'est déjà un nouveau status
  if (PIPELINE_STAGES.some(s => s.id === status)) {
    return status as PipelineStage;
  }
  
  // Sinon, mapper depuis l'ancien format
  return LEGACY_STATUS_MAP[status] || 'nouveau';
}

// Helper pour obtenir la config d'un stage
export function getStageConfig(status: string | undefined | null) {
  const normalized = normalizeStatus(status);
  return PIPELINE_STAGES.find(s => s.id === normalized) || PIPELINE_STAGES[0];
}
